/**
 * SA-1338 (spec Part 1): Done on a task opened from its own record goes back to
 * the record's own history entry (`navigate(-origin.back)`) instead of pushing
 * or replacing, so one Back from the record leaves it. The browser's Back lands
 * on that entry as it was, `?step=` and all, so the task page leaves a pending
 * landing here first, and the Quotes page, arriving, replaces its entry with
 * the record's landing (`landingPath`: the step and panel dropped,
 * `land=process` added).
 *
 * The pending landing lives at module level: the task page unmounts before the
 * Quotes page mounts, so it cannot ride either one's state. It is one shot: the
 * first location the hook sees takes it, whatever that location is.
 */
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { landingPath } from './taskPageModel'

/** A pending landing older than this is dropped: the arrival it was left for
 * never came. */
export const BACK_LANDING_MS = 5000

let pending: { orderId: string; at: number } | null = null

/** Called by the task page right before it goes back to the record's entry. */
export function holdBackLanding(orderId: string): void {
  pending = { orderId, at: Date.now() }
}

/** Tests: forget any pending landing between cases. */
export function clearBackLanding(): void {
  pending = null
}

/** Mounted by the Quotes page. On every location it takes the pending landing;
 * when that landing is fresh and the location names its record, the entry is
 * replaced with the record's landing, built from the URL that arrived, so
 * anything the entry holds besides the step and panel is kept.
 *
 * A passive effect, not a layout one: a memory router subscribes to its history
 * in its own layout effect, which runs after its children's on the first
 * commit, so a replace made from a child's layout effect then is never seen.
 * Either router commits the replace as a transition, so a layout effect would
 * paint the landing no sooner. */
export function useBackLanding(): void {
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const taken = pending
    pending = null
    if (!taken || Date.now() - taken.at > BACK_LANDING_MS) return
    const land = landingPath(`${location.pathname}${location.search}`, taken.orderId)
    if (land) navigate(land, { replace: true })
    // Keyed on the location's key alone: one look per arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])
}
